import React from 'react';
import PropTypes from 'prop-types';
import PricingRow from './PricingRow';
import ExtrasPricingRow from './ExtrasPricingRow';
import { Button, Divider, FormatCurrency, toast } from 'day8-ui';
import {
  CurrencyDropdown
} from 'day8-react/build/new-components';

import moment from 'moment';
import { useAutomaticDiscounts } from '../../../context/AutomaticDiscountsContext';
import {
  getDealOriginalTotalPrice,
  getDiscountedTotalPrice,
  getDiscountedStandardPrice,
  getDiscountedCabinPrice
} from '../../../helpers/pricing';
import { getSelectedPackageData } from '../../../helpers/week';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { BOOKING } from '../../../constants';
import { getPromoBookingSubjectFromLocation } from '../../../helpers/promoCode';
import { supportedCurrencies } from '../../../config';

const Pricing = ({
  yacht = undefined, crew, costs, isYachtCard = undefined, className = undefined, discount = undefined, totalPrice, nonVariableExtras = [], week, pricePerPerson, isCabin = false, standardYachtWeek
}) => {
  const location = useLocation();
  const destinations = useSelector(state => state.destinations.items);
  const guests = crew && crew.boys + crew.girls;
  const { getDiscountForWeekAndSubject } = useAutomaticDiscounts();
  const cabins = useSelector(state => state.cabins.data);

  // Deals and automatic promos must not stack (6.6 / 6.7).
  const isDealYacht = yacht?.isDeal === true;

  // Calculate discounted total price for automatic discounts
  let discountedTotalPrice = null;
  let originalTotalPrice = totalPrice;

  if (week && !isDealYacht) {
    const subject = isCabin ? getPromoBookingSubjectFromLocation(location) : BOOKING.YACHT_SUBJECT;
    const packageId = isCabin? getSelectedPackageData(destinations, location)?.id: (yacht?.package?.id ?? standardYachtWeek?.standardYacht?.package?.id);
    // A `yacht` (boat week) here is a BYO booking - gated on the campaign's BYO flag, not on packages
    const resolvedDiscount = getDiscountForWeekAndSubject(week.id, subject, packageId, !isCabin && !!yacht);

    if (resolvedDiscount) {
      if (yacht) {
        discountedTotalPrice = getDiscountedTotalPrice(yacht, crew, resolvedDiscount, week);
      } else if (standardYachtWeek) {
        discountedTotalPrice = getDiscountedStandardPrice(standardYachtWeek, week, crew, resolvedDiscount);
      } else if (isCabin && cabins && destinations && location) {
        discountedTotalPrice = getDiscountedCabinPrice(cabins, destinations, location, resolvedDiscount, week, getSelectedPackageData(destinations, location));
      }

      if (discountedTotalPrice !== null) {
        originalTotalPrice = totalPrice;
        totalPrice = discountedTotalPrice;
      }
    }
  }

  const dealOriginalTotalPrice = isDealYacht && yacht ? getDealOriginalTotalPrice(yacht, crew) : null;

  // Show discount if deal, automatic discount, or standard discount applies
  const showDealDiscount = dealOriginalTotalPrice !== null && !isYachtCard;
  const showAutomaticDiscount = discountedTotalPrice !== null && !isYachtCard;
  const showStandardDiscount = !!discount && !isYachtCard;
  const showDiscount = showDealDiscount || showAutomaticDiscount || showStandardDiscount;
  let hardcodedIncludedCosts = [];
  if (yacht && yacht.isBooked) {
    return (
      <div className={className}>
        <h3 className='dui-text-2xl'>Yacht not available</h3>
        <p className='dui-mt-4'>This yacht is not available for this week. It might be booked already.</p>
      </div>
    );
  }
  // push to every kind of booking included wristbands
  // hardcodedIncludedCosts.push({
  //   label: 'Yacht Week Wristbands',
  //   included: true
  // });
  const deadlines = isCabin ? week.cabinPaymentDeadlines : week.paymentDeadlines;
  const firstDeadline = deadlines?.length ? deadlines[0] : null;
  let priceToday = 0;
  let labelToday = "";
  if (firstDeadline && firstDeadline.postBookingDays > 0) {
    priceToday = 100;
    labelToday = `Due now:`;
    // Custom yacht rule
    // if (yacht) {
    //   priceToday = 0;
    // }
  }
  let discountPrice = null;
  if (showDealDiscount) {
    discountPrice = dealOriginalTotalPrice;
  } else if (showAutomaticDiscount) {
    discountPrice = originalTotalPrice;
  } else if (discount) {
    discountPrice = totalPrice + discount;
  }

  // Use discounted price for payment plan calculations if available
  const priceForPaymentPlan = discountedTotalPrice !== null ? discountedTotalPrice : totalPrice;

  const deadlineInfo = deadlines?.map((deadline, i) => {
    // cusotm yacht rule
    // if (yacht && i === 0) {
    //   return {
    //     label: "Due now",
    //     price: priceForPaymentPlan * firstDeadline.percentage / 100
    //   };
    // }
    return {
      // if deadline.date is today, show "Today"
      label: moment(deadline.date).isSame(moment(), 'day') ? "Due today:": `Due ${moment(deadline.date).format('DD MMM')} (${deadline.percentage}%):`,
      price: (priceForPaymentPlan * deadline.percentage / 100) - (i === 0 ? priceToday : 0)
    };
  }) || [];
  return (
    <>
      <div className={className}>
        <div className='dui-flex dui-justify-between dui-items-center'>
          <h3 className='dui-text-2xl'>Cost breakdown</h3>
          <CurrencyDropdown supportedCurrencies={supportedCurrencies} buttonType={"SECONDARY"} buttonSize={"EXTRA_SMALL"} toast={toast}  />
        </div>
        {costs && costs.map((cost, i) => <PricingRow key={i} {...cost} />)}
        {!!hardcodedIncludedCosts && hardcodedIncludedCosts.length > 0 && hardcodedIncludedCosts.map((hardcodedIncludedCost, i) => <PricingRow key={i} {...hardcodedIncludedCost} />)}
        {!!nonVariableExtras && nonVariableExtras.map(extra => (
          <ExtrasPricingRow
            key={extra.id}
            id={extra.id}
            label={extra.addIndividually ? `${extra.name} x${guests}` : extra.name}
            currency={week.currency}
            price={extra.addIndividually ? (extra.price * guests) / 100 : extra.price / 100}
            readMoreLink={extra.readMoreLink}
          />
        ))}
        <Divider className="dui-my-8" />
        <TotalPriceComponent
          totalPrice={totalPrice}
          showDiscount={showDiscount}
          discountPrice={discountPrice}
          baseCurrency={week.currency}
          label="Total:"
        />
        <PricingRow currency={week.currency} label={`Price per person (${guests}):`} price={pricePerPerson} />
      </div>
      {((deadlineInfo && deadlineInfo.length > 0) || priceToday > 0) && (
        <>
          <Divider className="dui-my-8" />
          <div>
            <h3 className='dui-text-2xl'>Payment plan*</h3>
            {priceToday > 0 && (
              <div className=''>
                <PricingRow currency={week.currency} label={labelToday} price={priceToday} />
              </div>
            )}
            {deadlineInfo.map(deadline => (
              <PricingRow key={deadline.label} currency={week.currency} label={deadline.label} price={deadline.price} />
            ))}
            <p className='dui-text-sm dui-text-gray-500'>*Alternative options are available. More information <a className='dui-text-sm' href="/how-it-works/step-by-step#payments" target="_blank" rel="noopener noreferrer">
              <Button className='!dui-inline !dui-text-sm' buttonType="LINK_UNDERLINE" buttonSize="SMALL">here</Button>
            </a></p>
          </div>
        </>
      )}

    </>
  );
};

Pricing.propTypes = {
  yacht: PropTypes.object,
  crew: PropTypes.object,
  costs: PropTypes.array,
  isYachtCard: PropTypes.bool,
  className: PropTypes.string,
  mandatoryExtras: PropTypes.array,
  isYachtsAndFriends: PropTypes.bool,
  discount: PropTypes.number,
  totalPrice: PropTypes.number,
  destinationId: PropTypes.number,
  baseCurrency: PropTypes.string,
  isAccountBookingPage: PropTypes.bool,
  nonVariableExtras: PropTypes.array,
  week: PropTypes.object,
  pricePerPerson: PropTypes.number,
  isCabin: PropTypes.bool,
  standardYachtWeek: PropTypes.object,
  location: PropTypes.object
};

const TotalPriceComponent = ({
  totalPrice, baseCurrency, label, discountPrice, showDiscount
}) => (
  <div className="dui-font-bold">
    {showDiscount && (
      <div className="dui-flex dui-justify-between  dui-gap-4">
        <div><p /></div>
        <div className="dui-text-right dui-text-gray-400">
          <s><p><FormatCurrency amount={discountPrice} baseCurrency={baseCurrency} /></p></s>
        </div>
      </div>
    )}
    <div className="dui-flex dui-justify-between  dui-gap-4">
      <div><p>{label}</p></div>
      <div className="dui-text-right">
        <span><p><FormatCurrency amount={totalPrice} baseCurrency={baseCurrency} /></p></span>
      </div>
    </div>
  </div>
);
TotalPriceComponent.propTypes = {
  totalPrice: PropTypes.number,
  baseCurrency: PropTypes.string,
  discountPrice: PropTypes.number,
  label: PropTypes.string,
  showDiscount: PropTypes.bool
};

export default Pricing;
